import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import MascotPlaceholder from "@/components/MascotPlaceholder";
import SupportMailLink from "@/components/SupportMailLink";
import { Button } from "@/components/ui/button";
import {
  Bell,
  PencilSimple,
  ListChecks,
  ArrowRight,
  Sparkle,
  ClockCounterClockwise,
  Calculator,
  Suitcase,
  Syringe,
  GraduationCap,
  AirplaneTakeoff,
  Briefcase,
} from "@phosphor-icons/react";

// Compute how many packing items are currently visible for the chosen zone
// and how many are checked. Mirrors the logic in /pages/Checkin.jsx so the
// counter matches exactly what the user sees inside the tool.
function computePackingCount(content, packing) {
  const zone = packing?.climate_zone;
  if (!content || !zone) return { done: 0, total: 0, ready: false };
  const checked = packing?.items || {};
  const customItems = packing?.custom_items || [];
  let done = 0;
  let total = 0;
  const seen = new Set();
  const addItem = (it) => {
    if (seen.has(it.id)) return;
    seen.add(it.id);
    total += 1;
    if (checked[it.id]) done += 1;
  };
  for (const section of content.packing?.sections || []) {
    if (!section.climate_specific) {
      for (const it of section.items || []) addItem(it);
    } else {
      const byZone = section.items_by_zone || {};
      if (zone === "unknown") {
        for (const z of ["northeast_midwest", "south_westcoast", "rural"]) {
          for (const it of byZone[z] || []) addItem(it);
        }
        for (const it of byZone.common || []) addItem(it);
      } else {
        for (const it of byZone[zone] || []) addItem(it);
        for (const it of byZone.common || []) addItem(it);
      }
    }
    // include user-added custom items for this section
    for (const ci of customItems.filter(c => c.section_id === section.id)) {
      addItem(ci);
    }
  }
  return { done, total, ready: true };
}

export default function Dashboard() {
  const { t, lang, user, loadingUser, phaseState, refreshPhaseState } = useApp();
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [latestQuiz, setLatestQuiz] = useState(null);
  const [profile, setProfile] = useState(null);
  const [countries, setCountries] = useState({ origin: [], destination: [] });
  const [profileChecked, setProfileChecked] = useState(false);
  const [budgetTotal, setBudgetTotal] = useState(null);   // null = not fetched, 0 = empty, >0 = has amounts
  const [docsCount, setDocsCount] = useState({ done: 0, total: 0 });
  const [vaxCount, setVaxCount] = useState({ done: 0, total: 0 });
  const [categoryBreakdown, setCategoryBreakdown] = useState({
    documents: { done: 0, total: 0 },
    vaccinations: { done: 0, total: 0 },
    school: { done: 0, total: 0 },
  });
  const [checkinContent, setCheckinContent] = useState(null);
  const [checkinState, setCheckinState] = useState(null);
  const [agencyOptions, setAgencyOptions] = useState([]);  // flat list of {id, name}

  useEffect(() => {
    if (loadingUser) return;
    if (!user) {
      navigate("/login", { state: { after: "/dashboard" } });
      return;
    }
    (async () => {
      let p = null;
      try {
        const r = await api.get("/departure-profile");
        p = r.data;
      } catch { /* profile absent */ }
      setProfile(p);
      setProfileChecked(true);
      if (!p) {
        navigate("/organizza", { replace: true });
        return;
      }
      api.get(`/checklists/reminders?lang=${lang}`).then(r => setReminders(r.data)).catch(() => {});
      api.get("/auth/me").then(r => setLatestQuiz(r.data.latest_quiz)).catch(() => {});
      api.get(`/content/countries?lang=${lang}`).then(r => setCountries(r.data)).catch(() => {});
      // Agency catalog — needed to resolve agency_id → human name on the
      // profile card. Comes back grouped by category; we flatten here.
      api.get(`/content/agency-options?lang=${lang}`).then(r => {
        const flat = [];
        for (const g of r.data || []) {
          for (const opt of g.items || []) flat.push(opt);
        }
        setAgencyOptions(flat);
      }).catch(() => {});
      api.get(`/checklists/my?phase=pre_departure&country=US&lang=${lang}`).then(r => {
        const items = r.data?.items || [];
        const total = items.length;
        const done = items.filter(i => i.checked).length;
        setDocsCount({ done, total });
        // Category breakdown for the sub-hint
        const byCat = (cat) => {
          const inCat = items.filter(i => (i.category || "documents") === cat);
          return { done: inCat.filter(x => x.checked).length, total: inCat.length };
        };
        setCategoryBreakdown({
          documents: byCat("documents"),
          vaccinations: byCat("vaccinations"),
          school: byCat("school"),
        });
      }).catch(() => {});
      // Legacy separate vax count kept at 0 — it's now inside the unified list
      setVaxCount({ done: 0, total: 0 });
      api.get("/checkin/state").then(r => {
        setCheckinState(r.data);
        const rows = [...(r.data?.budget?.fixed || []), ...(r.data?.budget?.us || [])];
        const total = rows
          .map(x => Number(x.amount))
          .filter(v => !Number.isNaN(v) && v > 0)
          .reduce((a, b) => a + b, 0);
        setBudgetTotal(total);
      }).catch(() => setBudgetTotal(0));
      api.get(`/checkin/content?lang=${lang}`).then(r => setCheckinContent(r.data)).catch(() => {});
      refreshPhaseState();
    })();
  }, [lang, user, loadingUser, navigate, refreshPhaseState]);

  if (loadingUser || !user || !profileChecked || !profile) {
    return <div className="p-8 text-ink-soft">{t.common.loading}</div>;
  }

  const firstName = (user.name || "").split(" ")[0] || user.email;
  const originName = (countries.origin.find(c => c.code === profile.origin_country) || {}).name || profile.origin_country;
  const destName = (countries.destination.find(c => c.code === profile.destination_country) || {}).name || profile.destination_country;
  const durationLabel = t.modes.organizzativa[
    profile.duration === "full_year" ? "durationFullYear"
    : profile.duration === "semester" ? "durationSemester"
    : "durationTrimester"
  ];
  const diplomaLabel = profile.diploma_goal === "us_diploma"
    ? t.modes.organizzativa.profileDiplomaUS
    : t.modes.organizzativa.profileDiplomaIT;
  const italianYearLabel = t.modes.organizzativa[
    profile.italian_school_year === "third" ? "italianYearThird"
    : profile.italian_school_year === "fourth" ? "italianYearFourth"
    : profile.italian_school_year === "fifth" ? "italianYearFifth"
    : ""
  ] || profile.italian_school_year;

  // Resolve agency_id → display name:
  //  • "other" + agency_other filled    → user's free-text value
  //  • "other" + no free-text           → translated "Altra agenzia"/"Other agency"
  //  • a known catalog id (ag_1 … ag_5) → the catalog's localized name
  //  • unknown or empty                 → "—"
  const resolveAgencyLabel = () => {
    const id = profile.agency_id;
    if (!id) return "";
    if (id === "other") {
      return profile.agency_other
        || t.modes.organizzativa.profileAgencyOtherDisplay;
    }
    const match = agencyOptions.find(a => a.id === id);
    return match?.name || id;
  };
  const agencyLabel = resolveAgencyLabel();

  // Checklist counter fed by the shared phaseState (already refreshed on load).
  const checkinUnlocked = !!phaseState?.checkin?.unlocked;
  const packingCount = computePackingCount(checkinContent, checkinState?.packing);
  const depCustom = checkinState?.departure?.custom_items || [];
  const departureTotal = (checkinContent?.departure_docs?.length || 0) + depCustom.length;
  const departureDone = (() => {
    const items = checkinState?.departure?.items || {};
    const seedDone = (checkinContent?.departure_docs || []).filter(d => items[d.id]).length;
    const customDone = depCustom.filter(ci => items[ci.id]).length;
    return seedDone + customDone;
  })();

  // "Scadenze imminenti" preview inside the checklist summary: items with a
  // user-set deadline within the next 14 days. Max 2.
  const imminentReminders = reminders
    .filter(r => typeof r.days_left === "number" && r.days_left >= 0 && r.days_left <= 14)
    .slice(0, 2);

  // "Prossime scadenze" at the top of the Bacheca: closest 3 items with
  // overdue tasks surfaced first (they already have `urgency === "overdue"`
  // server-side). Shown ONLY when the user has a school_year_abroad set in
  // the profile — otherwise we show an invite to configure it.
  const overdueCount = reminders.filter(r => typeof r.days_left === "number" && r.days_left < 0).length;
  const topReminders = [...reminders]
    .sort((a, b) => {
      const aOver = a.urgency === "overdue" ? 0 : 1;
      const bOver = b.urgency === "overdue" ? 0 : 1;
      if (aOver !== bOver) return aOver - bOver;
      return (a.days_left ?? 999) - (b.days_left ?? 999);
    })
    .slice(0, 3);
  const departureDateSet = !!profile.school_year_abroad;

  // Decide which category rows to render inside the checklist summary's
  // sub-counter: only categories with at least 1 task. Hide the whole row
  // when none has any task.
  const categoryParts = [
    { label: t.modes.organizzativa.catDocuments,    data: categoryBreakdown.documents },
    { label: t.modes.organizzativa.catVaccinations, data: categoryBreakdown.vaccinations },
    { label: t.modes.organizzativa.catSchool,       data: categoryBreakdown.school },
  ].filter(p => p.data.total > 0);
  const subCounterText = categoryParts.length
    ? categoryParts.map(p => `${p.label} ${p.data.done}/${p.data.total}`).join(" · ")
    : null;

  return (
    <div className="max-w-5xl mx-auto px-5 py-8 space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold font-sans">
            {t.nav.dashboard}
          </p>
          <h1 data-testid="dashboard-welcome" className="font-display font-black text-3xl sm:text-4xl text-ink mt-1 tracking-tight">
            {t.dashboard.welcome(firstName)}
          </h1>
        </div>
        <MascotPlaceholder emotion="celebrating" src="/assets/boardy-energico.png" />
      </div>

      {/* b) Prossime scadenze — moved to the top of the Bacheca. Shows the
             compact progress counter, up to 3 upcoming items (overdue first),
             and the "Vedi tutte" link. When school_year_abroad is empty we
             show a CTA to set it instead of the deadlines. */}
      <section data-testid="dashboard-reminders">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Bell size={22} weight="duotone" className="text-terracotta shrink-0" />
            <h2 className="font-display font-extrabold text-2xl text-ink tracking-tight">
              {t.dashboard.reminders}
            </h2>
          </div>
          {departureDateSet && reminders.length > 0 && (
            <button
              type="button"
              data-testid="dashboard-reminders-view-all"
              onClick={() => navigate("/organizza")}
              className="text-sm font-sans font-semibold text-terracotta hover:text-terracotta-hover inline-flex items-center gap-1"
            >
              {t.dashboard.viewAll}
              <ArrowRight size={14} weight="bold" />
            </button>
          )}
        </div>

        {departureDateSet && docsCount.total > 0 && (
          <div
            data-testid="dashboard-reminders-compact-counter"
            className="text-xs sm:text-sm font-sans text-ink-soft mb-3"
          >
            {t.dashboard.remindersCompactCounter(docsCount.done, docsCount.total, overdueCount)}
          </div>
        )}

        {!departureDateSet ? (
          <div
            data-testid="dashboard-departure-date-missing"
            className="rounded-2xl border border-dashed border-terracotta/40 bg-terracotta/5 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
          >
            <div className="min-w-0">
              <div className="font-display font-bold text-base sm:text-lg text-ink">
                {t.dashboard.departureDateMissingTitle}
              </div>
              <p className="text-sm text-ink-soft font-sans leading-relaxed mt-1 max-w-xl">
                {t.dashboard.departureDateMissingBody}
              </p>
            </div>
            <Button
              data-testid="dashboard-departure-date-cta"
              onClick={() => navigate("/organizza?edit=profile")}
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold shrink-0 w-full sm:w-auto"
            >
              <PencilSimple size={14} weight="duotone" className="mr-1" />
              {t.dashboard.departureDateMissingCta}
            </Button>
          </div>
        ) : topReminders.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-6 text-center text-ink-soft text-sm">
            {t.dashboard.noReminders}
          </div>
        ) : (
          <ul className="space-y-2">
            {topReminders.map(r => {
              const overdue = r.urgency === "overdue";
              return (
                <li
                  key={`${r.template_id}-${r.item_id}`}
                  data-testid={`reminder-${r.item_id}`}
                  className={`rounded-2xl p-4 border flex items-center justify-between gap-3 ${
                    overdue ? "bg-terracotta/10 border-terracotta/40"
                    : r.urgency === "soon" ? "bg-sand/30 border-sand/60"
                    : "bg-surface-card border-border"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-sans font-semibold text-ink min-w-0 break-words">
                        {r.text}
                      </span>
                      {overdue && (
                        <span
                          data-testid={`reminder-overdue-badge-${r.item_id}`}
                          className="text-[10px] uppercase tracking-wide font-sans font-bold text-white bg-terracotta rounded-full px-2 py-0.5 shrink-0"
                        >
                          {t.dashboard.remindersOverdueBadge}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-ink-soft mt-0.5">{new Date(r.deadline).toLocaleDateString()}</div>
                  </div>
                  <div className={`text-xs font-sans font-bold shrink-0 whitespace-nowrap ${
                    overdue ? "text-terracotta" : r.urgency === "soon" ? "text-terracotta" : "text-ink-soft"
                  }`}>
                    {r.days_left < 0 ? t.dashboard.daysAgo(Math.abs(r.days_left)) : t.dashboard.daysLeft(r.days_left)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* 1) Departure profile summary — moved below Check-in. Edit link stays. */}

      {/* Budget total widget — repositioned to live inside the "Organizza la partenza" group below */}

      {/* 2) Decision profile (from Quiz) — moved below Departure profile. */}

      {/* 3) Organizza la partenza — 3 sub-sections (docs, vax, school program)
             + budget widget as the 4th row. Each row is a compact summary that
             deep-links into the relevant page. */}
      <section
        data-testid="dashboard-organize-summary"
        className="rounded-3xl border border-border bg-surface-card p-5 sm:p-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <Briefcase size={20} weight="duotone" className="text-terracotta" />
          <h2 className="font-display font-extrabold text-xl text-ink tracking-tight">
            {t.dashboard.organizeSectionTitle}
          </h2>
        </div>

        <div className="divide-y divide-border/60">
          <SummaryRow
            testid="dashboard-organize-unified"
            icon={ListChecks}
            title={t.modes.organizzativa.unifiedTitle}
            counter={t.modes.organizzativa.checklistCounter(docsCount.done, docsCount.total)}
            subCounter={subCounterText}
            onClick={() => navigate("/organizza")}
            ctaLabel={t.dashboard.checklistSummaryCTA}
          />
          <BudgetRow
            testid="dashboard-organize-budget"
            t={t}
            budgetTotal={budgetTotal}
            onClick={() => navigate("/checkin?tool=budget")}
          />
        </div>

        {imminentReminders.length > 0 && (
          <div data-testid="dashboard-checklist-imminent" className="mt-5 pt-4 border-t border-border/60">
            <div className="text-[10px] uppercase tracking-widest text-terracotta font-sans font-bold mb-2">
              {t.dashboard.checklistSummaryUpcoming}
            </div>
            <ul className="space-y-2">
              {imminentReminders.map(r => (
                <li
                  key={`imm-${r.template_id}-${r.item_id}`}
                  data-testid={`dashboard-imminent-${r.item_id}`}
                  className="flex items-center justify-between gap-3"
                >
                  <span className="text-sm text-ink font-sans truncate">{r.text}</span>
                  <span className="text-xs font-sans font-bold text-terracotta shrink-0">
                    {t.dashboard.daysLeft(r.days_left)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* 4) Check-in summary — only visible once the phase is unlocked.
             Shows Packing list + Departure docs counters. */}
      {checkinUnlocked && (
        <section
          data-testid="dashboard-checkin-summary"
          className="rounded-3xl border border-border bg-surface-card p-5 sm:p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <AirplaneTakeoff size={20} weight="duotone" className="text-terracotta" />
            <h2 className="font-display font-extrabold text-xl text-ink tracking-tight">
              {t.dashboard.checkinSectionTitle}
            </h2>
          </div>

          <div className="divide-y divide-border/60">
            <SummaryRow
              testid="dashboard-checkin-packing"
              icon={Suitcase}
              title={t.dashboard.checkinPackingTitle}
              counter={
                packingCount.ready
                  ? t.modes.organizzativa.checklistCounter(packingCount.done, packingCount.total)
                  : t.dashboard.checkinPackingEmpty
              }
              counterMuted={!packingCount.ready}
              onClick={() => navigate("/checkin?tool=packing")}
              ctaLabel={t.dashboard.checklistSummaryCTA}
            />
            <SummaryRow
              testid="dashboard-checkin-departure"
              icon={ListChecks}
              title={t.dashboard.checkinDepartureTitle}
              counter={t.modes.organizzativa.checklistCounter(departureDone, departureTotal)}
              onClick={() => navigate("/checkin?tool=departure")}
              ctaLabel={t.dashboard.checklistSummaryCTA}
            />
          </div>
        </section>
      )}

      {/* e) Profilo di partenza — moved here from the top; same data,
             unchanged layout. */}
      <section data-testid="dashboard-profile" className="rounded-3xl border border-border bg-surface-card p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="font-display font-bold text-lg text-ink" data-testid="dashboard-profile-school">
            {profile.home_school || `${originName} → ${destName}`}
          </div>
          <Button
            data-testid="dashboard-profile-edit"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/organizza?edit=profile")}
            className="rounded-full text-terracotta hover:bg-terracotta/5"
          >
            <PencilSimple size={16} weight="duotone" className="mr-1" />
            {t.modes.organizzativa.profileEdit}
          </Button>
        </div>
        <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm font-sans">
          {[
            { label: t.modes.organizzativa.profileOrigin,        value: originName },
            { label: t.modes.organizzativa.profileDestination,   value: destName },
            { label: t.modes.organizzativa.profileYearAbroad,    value: profile.school_year_abroad },
            { label: t.modes.organizzativa.profileDuration,      value: durationLabel },
            { label: t.modes.organizzativa.profileAgency,        value: agencyLabel },
            { label: t.modes.organizzativa.profileItalianYear,   value: italianYearLabel },
            { label: t.modes.organizzativa.profileDiplomaGoal,   value: diplomaLabel },
          ].map(row => (
            <div key={row.label} className="min-w-0">
              <dt className="text-[10px] uppercase tracking-widest text-ink-soft font-semibold">{row.label}</dt>
              <dd className="text-ink truncate">{row.value || "—"}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* f) Il tuo profilo decisionale — moved to the bottom. Raw profile_key
             parenthetical removed since users read it as noise. */}
      <section data-testid="dashboard-decision-profile" className="rounded-3xl border border-border bg-surface-card p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={18} weight="duotone" className="text-terracotta" />
          <h2 className="font-display font-extrabold text-xl text-ink tracking-tight">
            {t.dashboard.decisionProfileTitle}
          </h2>
        </div>
        <p className="text-ink-soft text-sm font-sans leading-relaxed">
          {t.dashboard.decisionProfileBody}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {latestQuiz && (
            <Button
              data-testid="dashboard-view-quiz-result"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/quiz/risultato")}
              className="rounded-full text-terracotta hover:bg-terracotta/5"
            >
              {t.dashboard.viewLatestQuiz}
            </Button>
          )}
          <Button
            data-testid="dashboard-retake-quiz"
            variant="outline"
            size="sm"
            onClick={() => navigate("/quiz")}
            className="rounded-full border-terracotta text-terracotta hover:bg-terracotta/5"
          >
            <ClockCounterClockwise size={14} weight="duotone" className="mr-1" />
            {t.dashboard.retakeQuiz}
          </Button>
          <Button
            data-testid="dashboard-review-cards"
            variant="outline"
            size="sm"
            onClick={() => navigate("/esplora")}
            className="rounded-full border-terracotta text-terracotta hover:bg-terracotta/5"
          >
            {t.dashboard.reviewCards}
          </Button>
        </div>
      </section>

      {/* Footer: contact support */}
      <div
        data-testid="dashboard-support-footer"
        className="pt-6 mt-2 border-t border-border/60"
      >
        <p className="text-xs sm:text-sm text-ink-soft/70 font-sans leading-relaxed">
          {t.common.dashboardSupportLead}{" "}
          <SupportMailLink variant="full" testid="dashboard-support-link" subject="Feedback dalla Bacheca" />
        </p>
      </div>
    </div>
  );
}

// --- Row-level building blocks used by the "Organizza" and "Check-in" cards.

function SummaryRow({ testid, icon: Icon, title, counter, counterMuted, subCounter, onClick, ctaLabel }) {
  return (
    <div
      data-testid={testid}
      className="flex items-center justify-between gap-3 py-3.5 first:pt-1 last:pb-1"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-2xl bg-sage/15 flex items-center justify-center shrink-0">
          <Icon size={20} weight="duotone" className="text-sage" />
        </div>
        <div className="min-w-0">
          <div className="font-sans font-semibold text-ink truncate">{title}</div>
          <div
            data-testid={`${testid}-counter`}
            className={`text-xs font-sans mt-0.5 ${counterMuted ? "italic text-ink-soft/70" : "text-ink-soft"}`}
          >
            {counter}
          </div>
          {subCounter && (
            <div
              data-testid={`${testid}-subcounter`}
              className="text-[10px] text-ink-soft/70 font-sans mt-1 tracking-wide"
            >
              {subCounter}
            </div>
          )}
        </div>
      </div>
      <Button
        data-testid={`${testid}-cta`}
        variant="ghost"
        size="sm"
        onClick={onClick}
        className="rounded-full text-terracotta hover:bg-terracotta/5 shrink-0"
      >
        {ctaLabel}
        <ArrowRight size={14} weight="bold" className="ml-1" />
      </Button>
    </div>
  );
}

function BudgetRow({ testid, t, budgetTotal, onClick }) {
  const hasBudget = budgetTotal && budgetTotal > 0;
  return (
    <div
      data-testid={testid}
      className="flex items-center justify-between gap-3 py-3.5 first:pt-1 last:pb-1"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-2xl bg-terracotta/10 flex items-center justify-center shrink-0">
          <Calculator size={20} weight="duotone" className="text-terracotta" />
        </div>
        <div className="min-w-0">
          <div className="font-sans font-semibold text-ink truncate">
            {t.dashboard.budgetTitle}
          </div>
          {hasBudget ? (
            <div className="mt-0.5 flex items-baseline gap-2 flex-wrap">
              <span
                data-testid="dashboard-budget-total"
                className="font-display font-black text-lg text-ink"
              >
                € {String(Math.round(budgetTotal)).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
              </span>
              <span className="text-[11px] text-ink-soft font-sans">
                {t.dashboard.budgetSubtitle}
              </span>
            </div>
          ) : (
            <div
              data-testid="dashboard-budget-empty"
              className="text-xs text-ink-soft font-sans italic mt-0.5"
            >
              {t.dashboard.budgetEmptyMessage}
            </div>
          )}
        </div>
      </div>
      <Button
        data-testid="dashboard-budget-cta"
        variant="ghost"
        size="sm"
        onClick={onClick}
        className="rounded-full text-terracotta hover:bg-terracotta/5 shrink-0"
      >
        {hasBudget ? t.dashboard.budgetOpenCta : t.dashboard.budgetEmptyCta}
        <ArrowRight size={14} weight="bold" className="ml-1" />
      </Button>
    </div>
  );
}

